<template>
  <div class="payment-paypal">
    <zm-alert v-if="paymentError" type="error" show-icon :closable="false" class="payment-paypal__error">
      <div v-html="paymentError" />
    </zm-alert>
    <p class="payment-paypal__desc">{{ __$t('billing.payment.paypal_pending_desc') }}</p>
    <slot name="payment-method-error"></slot>
    <div class="payment-paypal__action">
      <zm-button
        round
        :loading="paymentResponseLoading"
        class="payment-paypal__action__continue"
        :disabled="saveDisabled"
        aria-label="Pay with paypal"
        @click="handleAddPayPal"
      >
        <span class="payment-paypal__action__continue__label">{{ __$t('billing.payment.pay_with') }}</span>
        <img :src="paypalIcon" alt="" />
      </zm-button>
    </div>
    <zm-dialog width="500px" :visible="paymentResponseLoading" class="payment__waiting-dialog" append-to-body>
      <h2 class="payment__waiting-dialog__title">{{ __$t('billing.payment.waiting_auth_pay_method_title') }}</h2>
      <p class="payment__waiting-dialog__desc">{{ __$t('billing.payment.waiting_auth_pay_method_desc') }}</p>
    </zm-dialog>
  </div>
</template>

<script type="text/jsx">
import {mapGetters} from 'vuex';
import {paypalPlan} from "../../apis/payment";
import {payMethodMap} from "../../common/constant";
import paypalIcon from "../../assets/paypal_logo.svg";
import PaymentBase from './common/PaymentBase';
import { APP_NAMESPACE } from '../../store';
import { translateMixins } from '../../plugins/i18n';

export default {
  name: 'PayPal',
  extends: PaymentBase,
  mixins: [translateMixins],
  data() {
    return {
      paypalIcon,
      // get payment gateway info loading
      paymentResponseLoading: false,
    };
  },
  computed: {
    ...mapGetters(APP_NAMESPACE, ['getHpmPassthroughValForBackupPayment']),
  },
  mounted() {
    this.ready();
  },
  methods: {
    togglePaymentResponseLoading(paymentResponseLoading) {
      this.paymentResponseLoading = paymentResponseLoading;
    },
    async initPayPal() {
      return new Promise((resolve, reject) => {
        const _this = this;
        this.togglePaymentResponseLoading(true);
        this.clearPaymentError();

        window.ZBilling = window.ZBilling || {};
        window.ZBilling.payPalAgreeCallback = (payerEmail, BAID) => {
          const paypalInfo = {
            payerEmail: payerEmail,
            BAID: BAID,
          };

          if(!this.isPrimary) {
            paypalInfo.field_passthrough7 = this.getHpmPassthroughValForBackupPayment(this.action)
          }

          const paymentMethod = {
            paymentType: payMethodMap.paypal,
            paymentGateway: 'PayPal',
            paymentResponse: JSON.stringify(paypalInfo),
          };

          this.togglePaymentResponseLoading(false);
          resolve(paymentMethod);
        };

        window.ZBilling.payPalCanceledCallback = function () {
          _this.togglePaymentResponseLoading(false);
          reject('canceled');
        };

        window.ZBilling.payPalErrorCallback = function ({errorMessage}) {
          _this.paymentError = errorMessage;
          _this.togglePaymentResponseLoading(false);
          reject(errorMessage);
        }

        const data = {
          description: '',
          successCallback: 'ZBilling.payPalAgreeCallback',
          cancelCallback: 'ZBilling.payPalCanceledCallback',
        };

        const payPalWindow = window.open('about:blank');

        if (payPalWindow === null || typeof payPalWindow === 'undefined') {
          window.ZBilling.payPalCanceledCallback();
          const errorMessage = this.__$t('billing.pop_up_blocked');
          alert(errorMessage);
          this.togglePaymentResponseLoading(false);
          return reject(errorMessage);
        }

        const payPalWindowDoc = payPalWindow.document;
        payPalWindowDoc.write(window.loadingPage || "");
        payPalWindowDoc.close();

        const payPalWindowPolling = setInterval(function () {
          if (payPalWindow.closed) {
            clearInterval(payPalWindowPolling);
            window.ZBilling.payPalCanceledCallback();
          }
        }, 1000);

        paypalPlan(data).then(res => {
          const result = res?.result;

          if (payPalWindow == null || typeof (payPalWindow) == 'undefined') {
            window.ZBilling.payPalCanceledCallback();
            alert(this.__$t('billing.pop_up_blocked'));
          } else {
            result?.agreementUrl && (payPalWindow.location.href = result?.agreementUrl);
          }
        }).catch(e => {
          payPalWindow.close();
          this.paymentError = e.message;
          window.ZBilling.payPalCanceledCallback();
        });
      });
    },
    async handleAddPayPal() {
      this.$emit('authorize')
      // init PayPal page and get callback response
      const paymentInfo = await this.initPayPal();
      this.$emit('submit-success', paymentInfo);
    },
    async $submit(paymentInfo) {
      return this.updatePayMethod(paymentInfo);
    }
  },
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.payment {
  &__waiting-dialog {
    color: #232333;
    margin-top: 10%;

    &__title {
      font-weight: 700;
      font-size: 22px;
      margin-bottom: 16px;
    }

    &__desc {
      font-weight: 500;
      font-size: 14px;
    }
  }
}

.payment-paypal {
  padding-top: 16px;

  &__error {
    margin-bottom: 16px;
    color: #80384d;
    background-color: #f3c2d0;
  }

  &__desc {
    font-weight: 500;
    font-size: 14px;
    color: #00053d;
  }

  &__action {
    margin-top: 16px;

    @include media(mobile, tablet) {
      display: flex;
      flex-direction: column;
      &__cancel {
        margin-left: 0 !important;
      }
    }

    &__continue {
      &.zm-button--plain {
        width: 389px;
        height: 50px;
        background-color: #ffc439;
        &.is-disabled {
          background-color: #f1f4f6;
        }
      }

      @include media(mobile) {
        width: 100%;
        &.zm-button--plain {
          width: 100%;
          height: 50px;
        }
      }
      &:hover {
        filter: brightness(0.95);
        background-color: #ffc439;
        border-color: #ffc439;
      }

      &.is-round {
        border-radius: 100px;
      }

      .zm-button__slot {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &__label {
        margin-right: 8px;
        font-size: 16px;
        font-weight: 600;
      }

      img {
        width: 70px;
        height: 22px;
      }
    }

    &__cancel {
      @include media(mobile, tablet) {
        margin-top: 16px;
        width: 100%;
      }
      &.zm-button {
        margin-left: 16px;
        @include media(mobile, tablet) {
          margin-left: 0;
        }
      }
    }
  }
}
</style>
